import type { jsPDF } from 'jspdf';
import {
  createPdfDoc,
  drawTextCell,
  PDF_BODY_SIZE,
  PDF_CELL_PADDING,
  PDF_CONTENT_WIDTH,
  PDF_GRID_COLOR,
  PDF_HEADER_FILL,
  PDF_LINE_HEIGHT,
  PDF_MARGIN,
  PDF_PAGE_HEIGHT,
  PDF_PAGE_WIDTH,
  PDF_TEXT_COLOR,
  PDF_TEXT_COLOR_MUTED,
  wrapText,
} from '@/shared/print';
import { i18n } from '@/shared/i18n';
import { MEALS, type MealId } from '@/features/menu/types';
import type { Recipe } from '@/features/ttk/types';
import type { DishDistributionRow } from './types';

/**
 * «Задание на кухню»: документ для поваров по раздаче на дату.
 *
 * Готовят в одном месте на объект и его подобъекты, поэтому на каждое блюдо
 * (в рамках приёма пищи) печатается: сколько каждого продукта взять на всю
 * закладку — брутто и нетто из ТТК, умноженные на число порций, — и куда
 * отдать готовое: подобъект, отделение, рацион, порции и килограммы.
 * Ничего не досчитывается сверх раздачи бэкенда и карты ТТК.
 */

const MARGIN_TOP = 18;
const MARGIN_BOTTOM = 16;
const BODY_MAX_Y = PDF_PAGE_HEIGHT - MARGIN_BOTTOM;
const ROW_HEIGHT = PDF_LINE_HEIGHT + PDF_CELL_PADDING * 2;

const MEAL_ORDER: Record<MealId, number> = Object.fromEntries(
  MEALS.map((meal, index) => [meal.id, index])
) as Record<MealId, number>;

const MEAL_LABEL_KEYS: Record<MealId, string> = Object.fromEntries(
  MEALS.map((meal) => [meal.id, meal.labelKey])
) as Record<MealId, string>;

interface Column {
  title: string;
  width: number;
  align?: 'right';
}

/** Блюдо в одном приёме пищи: одна закладка на кухне. */
interface KitchenDish {
  dishName: string;
  mealId: MealId;
  /** Чистый выход порции, граммы; 0 — не задан. */
  yieldNet: number;
  portions: number;
  places: DishDistributionRow[];
  recipe: Recipe | null;
}

/** Количество по-русски: до трёх знаков после запятой, без лишних нулей. */
function formatQty(value: number): string {
  return value.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

function kilogramsOf(portions: number, yieldNet: number): number | null {
  return yieldNet > 0 ? (portions * yieldNet) / 1000 : null;
}

/** Строки раздачи → блюда по приёмам пищи, в порядке дня, затем по алфавиту. */
function groupDishes(
  rows: DishDistributionRow[],
  recipes: ReadonlyMap<string, Recipe>
): KitchenDish[] {
  const byKey = new Map<string, KitchenDish>();
  for (const row of rows) {
    const key = `${row.mealId}\u0000${row.dishName}`;
    const dish = byKey.get(key) ?? {
      dishName: row.dishName,
      mealId: row.mealId,
      yieldNet: row.yieldNet,
      portions: 0,
      places: [],
      recipe: recipes.get(row.dishName.trim().toLowerCase()) ?? null,
    };
    dish.portions += row.portions;
    if (row.portions > 0) dish.places.push(row);
    byKey.set(key, dish);
  }

  return [...byKey.values()]
    .filter((dish) => dish.portions > 0)
    .sort(
      (a, b) =>
        MEAL_ORDER[a.mealId] - MEAL_ORDER[b.mealId] || a.dishName.localeCompare(b.dishName, 'ru')
    );
}

/** Рисует документ в переданный jsPDF (A4, миллиметры). */
export function writeKitchenPdf(
  doc: jsPDF,
  data: {
    displayDate: string;
    objectName: string;
    rows: DishDistributionRow[];
    recipes: ReadonlyMap<string, Recipe>;
  }
): void {
  let y = MARGIN_TOP;

  const newPage = () => {
    doc.addPage();
    y = MARGIN_TOP;
  };

  const setBody = (color = PDF_TEXT_COLOR, weight: 'normal' | 'bold' = 'normal') => {
    doc.setFont('PTSans', weight);
    doc.setFontSize(PDF_BODY_SIZE);
    doc.setTextColor(...color);
  };

  /** Таблица с переносом по страницам; шапка повторяется на новой странице. */
  const drawTable = (columns: Column[], rows: string[][]) => {
    const xs = [PDF_MARGIN];
    for (const column of columns) xs.push(xs[xs.length - 1] + column.width);

    const drawCells = (cells: string[][], top: number) => {
      cells.forEach((lines, index) => {
        drawTextCell(
          doc,
          lines,
          xs[index] + PDF_CELL_PADDING,
          xs[index + 1] - PDF_CELL_PADDING,
          top,
          columns[index].align === 'right'
        );
      });
    };

    const drawVerticals = (top: number, bottom: number) => {
      doc.setDrawColor(...PDF_GRID_COLOR);
      for (const x of xs) doc.line(x, top, x, bottom);
    };

    const drawHeader = () => {
      doc.setFillColor(...PDF_HEADER_FILL);
      doc.rect(PDF_MARGIN, y, PDF_CONTENT_WIDTH, ROW_HEIGHT, 'F');
      doc.setDrawColor(...PDF_GRID_COLOR);
      doc.line(PDF_MARGIN, y, PDF_MARGIN + PDF_CONTENT_WIDTH, y);
      setBody(PDF_TEXT_COLOR, 'bold');
      drawCells(
        columns.map((column) => [column.title]),
        y
      );
      y += ROW_HEIGHT;
      doc.line(PDF_MARGIN, y, PDF_MARGIN + PDF_CONTENT_WIDTH, y);
    };

    let tableTop = y;
    drawHeader();
    for (const row of rows) {
      setBody();
      const cells = row.map((text, index) =>
        wrapText(doc, text, columns[index].width - PDF_CELL_PADDING * 2)
      );
      const height =
        Math.max(...cells.map((lines) => lines.length)) * PDF_LINE_HEIGHT + PDF_CELL_PADDING * 2;
      if (y + height > BODY_MAX_Y) {
        drawVerticals(tableTop, y);
        newPage();
        tableTop = y;
        drawHeader();
        setBody();
      }
      drawCells(cells, y);
      y += height;
      doc.setDrawColor(...PDF_GRID_COLOR);
      doc.line(PDF_MARGIN, y, PDF_MARGIN + PDF_CONTENT_WIDTH, y);
    }
    drawVerticals(tableTop, y);
  };

  /** Подзаголовок таблицы («Продукты», «Куда отдать»). */
  const drawCaption = (text: string) => {
    setBody(PDF_TEXT_COLOR_MUTED, 'bold');
    doc.text(text, PDF_MARGIN, y + PDF_LINE_HEIGHT);
    y += PDF_LINE_HEIGHT + 1.6;
  };

  // Шапка документа: название, объект, дата.
  doc.setFont('PTSans', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...PDF_TEXT_COLOR);
  doc.text(i18n.t('portions.kitchenTitle'), PDF_MARGIN, y);
  doc.text(data.displayDate, PDF_PAGE_WIDTH - PDF_MARGIN, y, { align: 'right' });
  y += 6;
  if (data.objectName) {
    setBody(PDF_TEXT_COLOR_MUTED);
    doc.text(data.objectName, PDF_MARGIN, y);
    y += 4;
  }
  y += 4;

  const productColumns: Column[] = [
    { title: i18n.t('portions.kitchenColProduct'), width: 74 },
    { title: i18n.t('portions.kitchenColUnit'), width: 16 },
    { title: i18n.t('portions.kitchenColGrossPortion'), width: 32, align: 'right' },
    { title: i18n.t('portions.kitchenColGrossTotal'), width: 32, align: 'right' },
    { title: i18n.t('portions.kitchenColNetTotal'), width: 32, align: 'right' },
  ];
  const placeColumns: Column[] = [
    { title: i18n.t('portions.colObject'), width: 66 },
    { title: i18n.t('portions.colDepartment'), width: 48 },
    { title: i18n.t('portions.colDiet'), width: 38 },
    { title: i18n.t('portions.colPortions'), width: 18, align: 'right' },
    { title: i18n.t('portions.kg'), width: 16, align: 'right' },
  ];

  for (const dish of groupDishes(data.rows, data.recipes)) {
    // Блюдо целиком переносится на новую страницу, если там оно поместится;
    // длинное блюдо всё равно не начинаем без шапки и пары строк таблицы.
    const ingredientRows = dish.recipe?.ingredients.length ?? 0;
    const captionHeight = PDF_LINE_HEIGHT + 1.6;
    const blockHeight =
      11 +
      captionHeight +
      (ingredientRows > 0 ? (ingredientRows + 1) * ROW_HEIGHT : PDF_LINE_HEIGHT + 1) +
      3 +
      captionHeight +
      (dish.places.length + 1) * ROW_HEIGHT;
    const fitsOnPage = blockHeight <= BODY_MAX_Y - MARGIN_TOP;
    if (y + blockHeight > BODY_MAX_Y && (fitsOnPage || y + 10 + ROW_HEIGHT * 4 > BODY_MAX_Y)) {
      newPage();
    }

    const totalKg = kilogramsOf(dish.portions, dish.yieldNet);
    const meta = [
      i18n.t(MEAL_LABEL_KEYS[dish.mealId]),
      i18n.t('portions.kitchenPortions', { count: dish.portions }),
      dish.yieldNet > 0 ? i18n.t('portions.kitchenYield', { grams: dish.yieldNet }) : null,
      totalKg !== null ? i18n.t('portions.kitchenTotalKg', { kg: formatQty(totalKg) }) : null,
    ]
      .filter(Boolean)
      .join(' · ');

    doc.setFont('PTSans', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...PDF_TEXT_COLOR);
    const titleLines = wrapText(doc, dish.dishName, PDF_CONTENT_WIDTH);
    doc.text(titleLines, PDF_MARGIN, y + 4);
    y += titleLines.length * 5;
    setBody(PDF_TEXT_COLOR_MUTED);
    doc.text(meta, PDF_MARGIN, y + 3.4);
    y += 6;

    drawCaption(i18n.t('portions.kitchenProducts'));
    if (dish.recipe && dish.recipe.ingredients.length > 0) {
      drawTable(
        productColumns,
        dish.recipe.ingredients.map((ingredient) => [
          ingredient.productName,
          ingredient.unit,
          formatQty(ingredient.gross),
          formatQty(ingredient.gross * dish.portions),
          formatQty(ingredient.net * dish.portions),
        ])
      );
    } else {
      setBody(PDF_TEXT_COLOR_MUTED);
      doc.text(i18n.t('portions.kitchenNoRecipe'), PDF_MARGIN, y + PDF_LINE_HEIGHT);
      y += PDF_LINE_HEIGHT + 1;
    }
    y += 3;

    if (y + PDF_LINE_HEIGHT + ROW_HEIGHT * 2 > BODY_MAX_Y) newPage();
    drawCaption(i18n.t('portions.kitchenWhere'));
    drawTable(
      placeColumns,
      dish.places.map((place) => {
        const kg = kilogramsOf(place.portions, place.yieldNet);
        // Без выбранного объекта (вся организация) подобъект подписан объектом.
        const placeName =
          data.objectName || place.subObjectName === place.objectName
            ? place.subObjectName
            : `${place.subObjectName} · ${place.objectName}`;
        return [
          placeName,
          place.departmentName,
          place.tableName,
          String(place.portions),
          kg === null ? '' : formatQty(kg),
        ];
      })
    );
    y += 8;
  }

  // Подвал: подпись повара.
  if (y + 12 > BODY_MAX_Y) newPage();
  setBody(PDF_TEXT_COLOR_MUTED);
  doc.text(i18n.t('portions.kitchenCook'), PDF_MARGIN, y + PDF_LINE_HEIGHT);
}

/** Скачивает «Задание на кухню» как PDF. */
export async function downloadKitchenDoc(data: {
  date: string;
  objectName: string;
  rows: DishDistributionRow[];
  recipes: ReadonlyMap<string, Recipe>;
}): Promise<void> {
  const displayDate = data.date.split('-').reverse().join('.');
  const doc = await createPdfDoc();
  writeKitchenPdf(doc, { ...data, displayDate });
  doc.save(i18n.t('portions.kitchenFileName', { date: displayDate }));
}
